'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

type Slot = {
  label: string;
  value: string;
  minutes: number;
  blocked: boolean;
  blockReason?: 'past' | 'booked';
};

export type TimeRange = { start: string | null; end: string | null };

type Busy = { start: string; end: string; reason?: 'past' | 'booked' };

type Props = {
  openFrom?: string;
  openTo?: string;
  intervalMinutes?: number;
  initial?: TimeRange;
  minDurationMinutes?: number;
  busyIntervals?: Busy[];
  quickPicks?: number[];
  onChange?: (range: Required<TimeRange>) => void;
  className?: string;
};

function toMinutes(hhmm: string) {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function toLabel(mins: number) {
  const h = Math.floor(mins / 60).toString().padStart(2, '0');
  const m = (mins % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

// Strict overlap: two intervals overlap only if they share interior — touching endpoints do NOT overlap.
// This allows a booking to end exactly when another starts.
function strictOverlaps(aStart: number, aEnd: number, bStart: number, bEnd: number) {
  return aStart < bEnd && bStart < aEnd;
}

export default function TimeRangePicker({
  openFrom = '08:00',
  openTo = '23:00',
  intervalMinutes = 30,
  initial = { start: null, end: null },
  minDurationMinutes = 30,
  busyIntervals = [],
  quickPicks = [60, 90, 120],
  onChange,
  className,
}: Props) {
  const [start, setStart] = useState<string | null>(initial.start);
  const [end, setEnd] = useState<string | null>(initial.end);
  const [hoverM, setHoverM] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const liveRef = useRef<HTMLDivElement>(null);

  const fromM = toMinutes(openFrom);
  const toM = toMinutes(openTo);

  const busyM = useMemo(
    () => busyIntervals.map(b => ({ s: toMinutes(b.start), e: toMinutes(b.end), reason: b.reason })),
    [busyIntervals]
  );

  // A slot is "blocked" (grey/red, cannot be selected as start) if it falls within a busy interval.
  // We use strict overlap: slot [m, m+interval) overlaps busy [s,e) if m < e && s < m+interval.
  const slots: Slot[] = useMemo(() => {
    const arr: Slot[] = [];
    for (let m = fromM; m <= toM; m += intervalMinutes) {
      const hit = busyM.find(b => strictOverlaps(m, m + intervalMinutes, b.s, b.e));
      arr.push({
        label: toLabel(m),
        value: toLabel(m),
        minutes: m,
        blocked: !!hit,
        blockReason: hit?.reason,
      });
    }
    return arr;
  }, [fromM, toM, intervalMinutes, busyM]);

  const startM = start ? toMinutes(start) : null;
  const endM = end ? toMinutes(end) : null;

  function announce(msg: string) {
    if (liveRef.current) liveRef.current.textContent = msg;
  }

  // Check if range [aStart, aEnd) strictly overlaps any busy interval.
  function rangeConflicts(aStart: number, aEnd: number) {
    return busyM.some(b => strictOverlaps(aStart, aEnd, b.s, b.e));
  }

  // Whether a given minute value is a valid end time given a selected start.
  // Key fix: end time is the BOUNDARY of the booking — it sits at the start of the next slot.
  // So end=15:00 means "booking ends at 15:00", it does NOT occupy the 15:00 slot.
  // Therefore the range to conflict-check is [startM, endM) — strictly open at both ends vs busy.
  // A slot that is itself blocked CAN still be a valid end boundary.
  function isValidEnd(m: number): boolean {
    if (startM == null) return false;
    if (m <= startM) return false;
    if (m - startM < minDurationMinutes) return false;
    // Check that the RANGE [startM, m) does not conflict with any busy interval.
    return !rangeConflicts(startM, m);
  }

  // Whether a given minute is a valid start time (slot must not be blocked).
  const phase: 'start' | 'end' = startM == null ? 'start' : 'end';

  function handleSlotClick(slot: Slot) {
    setMessage(null);

    if (phase === 'start') {
      if (slot.blocked) {
        setMessage('Dieser Zeitraum ist bereits vergeben.');
        announce('Dieser Zeitraum ist bereits vergeben.');
        return;
      }
      setStart(slot.value);
      setEnd(null);
      announce(`Startzeit ${slot.value} gewählt. Jetzt Endzeit wählen.`);
      return;
    }

    // Phase: end selection
    const m = slot.minutes;

    // Allow clicking the start slot again to reset
    if (m === startM) {
      setStart(null);
      setEnd(null);
      setHoverM(null);
      announce('Auswahl zurückgesetzt.');
      return;
    }

    if (isValidEnd(m)) {
      setEnd(slot.value);
      setHoverM(null);
      announce(`Endzeit ${slot.value} gewählt.`);
      return;
    }

    // Clicked something invalid — if it's a free slot earlier or a conflict, treat as new start
    if (!slot.blocked && m > (startM ?? 0)) {
      setMessage('Dieser Zeitraum überschneidet sich mit einer Buchung. Wählen Sie eine andere Endzeit.');
      announce('Zeitraum überschneidet sich mit einer bestehenden Buchung.');
    } else if (!slot.blocked) {
      setStart(slot.value);
      setEnd(null);
      setHoverM(null);
      announce(`Startzeit auf ${slot.value} geändert.`);
    }
  }

  function handleMouseEnter(slot: Slot) {
    if (phase === 'end') setHoverM(slot.minutes);
  }

  useEffect(() => {
    if (start && end && onChange) onChange({ start, end });
  }, [start, end, onChange]);

  function applyQuickPick(durationMinutes: number) {
    if (startM == null) {
      setMessage('Wählen Sie zuerst eine Startzeit.');
      return;
    }
    const targetEnd = startM + durationMinutes;
    if (targetEnd > toM) {
      setMessage('Diese Dauer überschreitet die Öffnungszeiten.');
      return;
    }
    if (rangeConflicts(startM, targetEnd)) {
      setMessage('Diese Dauer überschneidet sich mit einer bestehenden Buchung.');
      return;
    }
    setEnd(toLabel(targetEnd));
    setHoverM(null);
    setMessage(null);
    announce(`Endzeit auf ${toLabel(targetEnd)} gesetzt.`);
  }

  function reset() {
    setStart(null);
    setEnd(null);
    setHoverM(null);
    setMessage(null);
    announce('Auswahl zurückgesetzt.');
  }

  const hasStart = startM != null;
  const hasEnd = endM != null;

  // Effective preview end (hover or confirmed)
  const previewEnd = hasEnd ? endM : (hoverM != null && hoverM > (startM ?? -1) ? hoverM : null);

  function getSlotRole(slot: Slot): 'start-selected' | 'end-selected' | 'in-range' | 'hover-range' | 'valid-end' | 'invalid-end' | 'blocked-past' | 'blocked-booked' | 'available' | 'boundary-end' {
    const m = slot.minutes;

    if (slot.blocked && slot.blockReason === 'past') return 'blocked-past';
    if (slot.blocked && slot.blockReason === 'booked') {
      // Special: if in end-selection phase, this blocked slot might be a valid END boundary
      if (phase === 'end' && isValidEnd(m)) return 'boundary-end';
      return 'blocked-booked';
    }

    if (hasStart && m === startM) return 'start-selected';
    if (hasEnd && m === endM) return 'end-selected';

    if (phase === 'end') {
      // In-range highlight (confirmed selection)
      if (hasEnd && m > startM! && m < endM!) return 'in-range';
      // Hover preview range
      if (!hasEnd && previewEnd != null && m > startM! && m < previewEnd) return 'hover-range';
      // Valid end slot
      if (isValidEnd(m)) return 'valid-end';
      // Invalid (too close, or overlaps busy)
      return 'invalid-end';
    }

    return 'available';
  }

  const durationLabel = () => {
    if (!hasStart || !hasEnd) return null;
    const mins = endM! - startM!;
    if (mins < 60) return `${mins} Min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h} Std` : `${h} Std ${m} Min`;
  };

  return (
    <div className={clsx('select-none', className)}>
      {/* ── Phase indicator / status bar ─────────────────────────────── */}
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {/* Step pills */}
          <div className="flex items-center gap-1.5">
            <div className={clsx(
              'flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold transition-all duration-300',
              hasStart
                ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-200'
                : 'bg-emerald-600 text-white shadow-sm shadow-emerald-200 ring-2 ring-emerald-300 ring-offset-1'
            )}>
              <span className={clsx(
                'flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-black',
                hasStart ? 'bg-white/30' : 'bg-white/20'
              )}>1</span>
              {hasStart ? (
                <span>{start}</span>
              ) : (
                <span>Startzeit</span>
              )}
            </div>

            <svg className="h-3 w-3 text-gray-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>

            <div className={clsx(
              'flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold transition-all duration-300',
              hasEnd
                ? 'bg-emerald-700 text-white shadow-sm shadow-emerald-200'
                : hasStart
                ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-200 ring-2 ring-emerald-300 ring-offset-1'
                : 'bg-gray-100 text-gray-400 border border-gray-200'
            )}>
              <span className={clsx(
                'flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-black',
                hasEnd ? 'bg-white/30' : hasStart ? 'bg-white/20' : 'bg-gray-200 text-gray-500'
              )}>2</span>
              {hasEnd ? (
                <span>{end}</span>
              ) : (
                <span>Endzeit</span>
              )}
            </div>

            {hasStart && hasEnd && durationLabel() && (
              <span className="ml-1 rounded-full bg-gray-100 border border-gray-200 px-2.5 py-1 text-[13px] font-semibold text-[color:var(--svh-ink-2)]">
                {durationLabel()}
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={reset}
          className="shrink-0 rounded-full px-2.5 py-1 text-[13px] font-medium text-[color:var(--svh-ink-3)] hover:text-[color:var(--svh-ink)] hover:bg-gray-100 transition-all"
        >
          Zurücksetzen
        </button>
      </div>

      {/* ── Instruction line ─────────────────────────────────────────── */}
      {(!hasStart || (!hasEnd && !message)) && (
        <div className="mb-2.5 flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
          <div className="h-5 w-5 shrink-0 rounded-full bg-blue-500 flex items-center justify-center">
            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01" />
            </svg>
          </div>
          <p className="text-[13px] text-blue-800 font-medium">
            {!hasStart
              ? 'Klicken Sie auf eine freie Zeit, um Ihre Startzeit zu wählen.'
              : 'Wählen Sie jetzt Ihre Endzeit — grün markierte Zeiten sind verfügbar.'}
          </p>
        </div>
      )}

      {/* ── Quick picks ──────────────────────────────────────────────── */}
      {quickPicks.length > 0 && (
        <div className="mb-3 flex items-center gap-2">
          <span className="svh-label shrink-0">Dauer</span>
          <div className="flex flex-wrap gap-1.5">
            {quickPicks.map((d) => {
              const targetEnd = startM != null ? startM + d : null;
              const available = targetEnd != null && targetEnd <= toM && !rangeConflicts(startM!, targetEnd);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => applyQuickPick(d)}
                  disabled={!hasStart}
                  className={clsx(
                    'rounded-full border px-3 py-1 text-[11px] font-bold transition-all duration-200',
                    !hasStart && 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed',
                    hasStart && available && 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 hover:border-emerald-400 hover:shadow-sm',
                    hasStart && !available && 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed line-through',
                  )}
                >
                  {d >= 60 ? `${d / 60}h` : `${d}m`}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Time grid ────────────────────────────────────────────────── */}
      <div
        id="time-grid"
        className="grid grid-cols-4 gap-1.5"
        role="listbox"
        aria-label="Zeitauswahl"
        onMouseLeave={() => setHoverM(null)}
      >
        {slots.map((slot, i) => {
          const role = getSlotRole(slot);
          const isDisabledAttr = role === 'blocked-past' || role === 'blocked-booked';

          return (
            <button
              key={slot.value}
              type="button"
              data-idx={i}
              data-value={slot.value}
              disabled={isDisabledAttr}
              onClick={() => handleSlotClick(slot)}
              onMouseEnter={() => handleMouseEnter(slot)}
              aria-pressed={role === 'start-selected' || role === 'end-selected'}
              aria-label={
                role === 'blocked-past' ? `${slot.label}, vergangen` :
                role === 'blocked-booked' ? `${slot.label}, belegt` :
                role === 'start-selected' ? `${slot.label}, Startzeit` :
                role === 'end-selected' ? `${slot.label}, Endzeit` :
                role === 'boundary-end' ? `${slot.label}, als Endzeit wählen` :
                role === 'valid-end' ? `${slot.label}, als Endzeit wählen` :
                role === 'invalid-end' ? `${slot.label}, nicht wählbar` :
                phase === 'end' ? `${slot.label}, als Endzeit wählen` :
                `${slot.label}, als Startzeit wählen`
              }
              className={clsx(
                'relative py-2 text-[12px] font-semibold transition-all duration-150 text-center',
                'rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1',

                // Blocked — past
                role === 'blocked-past' && [
                  'bg-gray-50 border-gray-150 text-gray-300 cursor-not-allowed',
                ],

                // Blocked — booked
                role === 'blocked-booked' && [
                  'bg-red-50 border-red-200 text-red-300 cursor-not-allowed',
                  'after:absolute after:inset-x-2 after:top-1/2 after:h-px after:bg-red-300 after:content-[""]',
                ],

                // Available (no start selected yet — clickable start)
                role === 'available' && [
                  'bg-white border-gray-200 text-gray-700',
                  'hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-800 hover:shadow-sm cursor-pointer',
                ],

                // Selected start
                role === 'start-selected' && [
                  'bg-emerald-600 border-emerald-600 text-white shadow-md shadow-emerald-200',
                  'scale-105 cursor-pointer',
                ],

                // Selected end
                role === 'end-selected' && [
                  'bg-emerald-700 border-emerald-700 text-white shadow-md shadow-emerald-200',
                  'scale-105 cursor-pointer',
                ],

                // In confirmed range
                role === 'in-range' && [
                  'bg-emerald-100 border-emerald-300 text-emerald-800 cursor-pointer',
                ],

                // Hover preview range
                role === 'hover-range' && [
                  'bg-emerald-50 border-emerald-200 text-emerald-700 cursor-pointer',
                ],

                // Valid end slot (pulsing to guide user)
                role === 'valid-end' && [
                  'bg-white border-emerald-300 text-emerald-700 cursor-pointer',
                  'hover:bg-emerald-600 hover:border-emerald-600 hover:text-white hover:shadow-md hover:scale-105',
                  'ring-1 ring-emerald-200',
                ],

                // Boundary end (blocked slot but valid as end boundary)
                role === 'boundary-end' && [
                  'bg-amber-50 border-amber-400 text-amber-700 cursor-pointer',
                  'hover:bg-amber-500 hover:border-amber-500 hover:text-white hover:shadow-md hover:scale-105',
                  'ring-1 ring-amber-300',
                ],

                // Invalid end (would cause conflict or too short)
                role === 'invalid-end' && [
                  'bg-white border-gray-200 text-gray-300 cursor-not-allowed opacity-40',
                ],
              )}
            >
              <span className="relative z-10">{slot.label}</span>

              {/* Start badge */}
              {role === 'start-selected' && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-emerald-900 px-1.5 py-px text-[11px] font-bold text-white shadow-sm whitespace-nowrap z-20 pointer-events-none">
                  START
                </span>
              )}

              {/* End badge */}
              {role === 'end-selected' && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-emerald-900 px-1.5 py-px text-[11px] font-bold text-white shadow-sm whitespace-nowrap z-20 pointer-events-none">
                  ENDE
                </span>
              )}

              {/* Boundary end hint badge */}
              {role === 'boundary-end' && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-amber-600 px-1.5 py-px text-[11px] font-bold text-white shadow-sm whitespace-nowrap z-20 pointer-events-none">
                  ENDE
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Legend ───────────────────────────────────────────────────── */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded border border-gray-200 bg-white" />
          <span className="text-[10px] font-medium text-gray-400">Frei</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded border border-emerald-300 bg-white ring-1 ring-emerald-200" />
          <span className="text-[10px] font-medium text-gray-400">Endzeit wählbar</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded border border-amber-400 bg-amber-50 ring-1 ring-amber-300" />
          <span className="text-[10px] font-medium text-gray-400">Als Endzeit möglich</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="relative h-3 w-5 rounded border border-red-200 bg-red-50">
            <div className="absolute inset-x-0.5 top-1/2 h-px bg-red-300" />
          </div>
          <span className="text-[10px] font-medium text-gray-400">Belegt</span>
        </div>
      </div>

      {/* ── Error / info message ─────────────────────────────────────── */}
      {message && (
        <div className="mt-2.5 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
          <svg className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-[12px] text-amber-800 font-medium">{message}</p>
        </div>
      )}

      <div ref={liveRef} className="sr-only" role="status" aria-live="polite" aria-atomic="true" />
    </div>
  );
}
